import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getSetupStatus, reverify } from "../../api/operatorSetup";

/**
 * Change a verified PAN or GSTIN (owner 2026-10-31): the new value must pass a
 * real check. A failed check changes nothing. The legal name follows the PAN.
 */
export function VerifiedDetailsCard({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["setup", ns], retry: false, queryFn: ({ signal }) => getSetupStatus(identity, ns, signal) });
  const [kind, setKind] = useState<"pan" | "gstin" | "cin">("gstin");
  const [value, setValue] = useState("");
  const check = useMutation({
    mutationFn: () => reverify(identity, ns, kind, value),
    onSuccess: () => {
      setValue("");
      void qc.invalidateQueries({ queryKey: ["setup", ns] });
      void qc.invalidateQueries({ queryKey: ["operator", ns] });
    },
  });
  if (!q.data) return null;
  return (
    <Paper sx={{ p: 2, mb: 2 }} data-testid="verified-details-card">
      <Typography variant="subtitle1">Verified details</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        PAN {q.data.pan || "not on file"} · GSTIN {q.data.gstin || "not on file"} · {q.data.taxverified ? "verified" : "not verified"}.
        {q.data.cin && ` CIN ${q.data.cin} · ${q.data.cinverified ? "verified" : "not verified"}.`}
        These change only by passing a new check. Your legal name comes from the PAN.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "flex-start" }}>
        <TextField select size="small" label="Check" value={kind} onChange={(e) => setKind(e.target.value as "pan" | "gstin" | "cin")}>
          <MenuItem value="gstin">GSTIN</MenuItem>
          <MenuItem value="pan">PAN</MenuItem>
          <MenuItem value="cin">CIN</MenuItem>
        </TextField>
        <TextField size="small" label={kind.toUpperCase()} value={value} onChange={(e) => setValue(e.target.value)} />
        <Button variant="outlined" disabled={check.isPending || !value.trim()} onClick={() => check.mutate()}>Verify</Button>
      </Stack>
      {check.isError && <Alert severity="error" sx={{ mt: 1 }}>{check.error instanceof Error ? check.error.message : "Could not verify."}</Alert>}
      {check.isSuccess && !check.isPending && <Alert severity="success" sx={{ mt: 1 }}>Verified and saved.</Alert>}
    </Paper>
  );
}
